import { DeviceService } from "@sureview/v2-cameras";
import { DeviceDetails } from "@/store/devices/types";
import { DeviceCamera, CameraRecordingState } from "@/store/video-reporting/types";
import api from "./api.service";
import moment from "moment";

export interface CameraRecordingOptions {
	cameraId: number;
	eventRecordId: number;
	recordingDate: Date;
	duration: number;
}

export interface CameraRecordingResult {
	state: CameraRecordingState;
	messages: string[];
}

export class VideoReportRecordingService {
	constructor(private reportEventId: number) {}

	private async getDeviceDetails(deviceId: number, eventRecordId: number): Promise<DeviceDetails> {
		const { data } = await api.loadDeviceById(deviceId, {
			eventId: this.reportEventId,
			noRecordingCamera: true
		});

		return {
			...data,
			eventRecordID: eventRecordId
		};
	}

	private async getDeviceUrl(
		deviceId: number,
		recordingDate: Date,
		duration: number,
		eventRecordId: number,
		auth: string
	): Promise<string> {
		let deviceDetails = await this.getDeviceDetails(deviceId, eventRecordId);
		let deviceService = new DeviceService(deviceDetails.deviceServerEndpoint);
		deviceService.authSessionID = auth;

		let deviceUrl = deviceService.cameraUrl(deviceDetails);

		// Add the formatted recording time to our URL, along with the timeZoneId of the device
		let recordingDateMoment = moment(recordingDate);
		let recordingDateFormatted = recordingDateMoment.format("YYYY-MM-DDTHH:mm:ss");
		deviceUrl += `&playFrom=${recordingDateFormatted}`;

		if (deviceDetails.timeZone) {
			deviceUrl += `&timeZoneId=${deviceDetails.timeZone}`;
		}

		// Add the duration
		deviceUrl += `&duration=${duration}`;

		// Add the remaining params
		deviceUrl += `&sendData=0&alarmRecorder=0`;

		return deviceUrl;
	}

	public async recordCamera(recordingOpts: CameraRecordingOptions, auth: string): Promise<CameraRecordingResult> {
		let { cameraId, recordingDate, duration, eventRecordId } = recordingOpts;

		// Generate a device URL, and open up a websocket for that device
		const deviceUrl = await this.getDeviceUrl(cameraId, recordingDate, duration, eventRecordId, auth);
		let videoSocket = new WebSocket(deviceUrl);

		console.log("Started for camera " + cameraId);

		const socketMessages = [];

		// We'll chain this promise onto the one that handles the web socket.
		// When the websocket closes or throws an error, this will be fired
		// and will ensure the socket is closed & disposed of.
		const cleanUpVideoSocket = (result: CameraRecordingResult) => {
			videoSocket.close();
			videoSocket = null;
			return result;
		};

		return new Promise(resolve => {
			console.log("Setting up websocket...");

			const resolveWithState = (state: CameraRecordingState) =>
				resolve({
					state,
					messages: socketMessages
				});

			videoSocket.onmessage = (event: MessageEvent) => {
				// Push the message
				socketMessages.push(event.data.split("\n")[0]);

				if (event.data.startsWith("500")) {
					// This message indicates an error occurred. Usually, if this was the video
					// player, we'd set a timeout and re-try in 5 seconds. However, as this a report
					// we fail-fast and report the camera as failed.

					// Check to see if it's a message indicating an error in CameraStartPlayback. If it is,
					// then chances are the integration/device could not find a clip. So we'll fail-fast and
					// reject with a NoClip state.
					if (event.data.startsWith("500 Action 'CameraStartPlayback")) {
						resolveWithState(CameraRecordingState.NoClip);
					} else {
						resolveWithState(CameraRecordingState.Failure);
					}
				}
			};

			videoSocket.onerror = (event: Event) => {};

			videoSocket.onclose = (event: CloseEvent) => {
				// Check the reason we closed, a non-1000 code indicates failure.
				if (event.code == 1008) {
					socketMessages.push("Socket was closed because you made a bad request");
					resolveWithState(CameraRecordingState.Failure);
				} else if (event.code == 1011) {
					socketMessages.push("Socket was closed due to a server error");
					resolveWithState(CameraRecordingState.Failure);
				} else if (event.code !== 1000) {
					socketMessages.push("Video socket closed.");
					resolveWithState(CameraRecordingState.Failure);
				} else {
					resolveWithState(CameraRecordingState.Success);
				}
			};
		}).then(cleanUpVideoSocket, cleanUpVideoSocket);
	}
}
